import { useCallback, useEffect, useState } from 'react';

import {
  browseVenues, venueMarkets, venueAvailability,
  type DirectoryVenue,
} from '../../lib/organizer';
import { ApiError } from '../../lib/apiClient';
import './OrganizerPages.css';

/**
 * Venue directory browser.
 *
 * Shared by the standalone browse screen and the venue section of event
 * management, because they are the same decision made in two places — a venue is
 * chosen against a specific event's date and expected size either way.
 *
 * Two things this deliberately shows:
 *
 *   The AVAILABILITY of the venue on the event's own date, fetched per venue when
 *   expanded. Requesting a room that is already blocked wastes both parties' time,
 *   and organizers are allowed this read by design.
 *
 *   That `approved` is inert. Every venue is currently unapproved because nothing
 *   in the engine can approve one, so the badge says "not reviewed" rather than
 *   implying a pending decision or, worse, hiding the venue.
 */

/** Postgres DATE arrives as an instant; compare on the calendar-date prefix only. */
const dateKey = (v: string) => String(v).slice(0, 10);

function AvailabilityNote({ venueId, eventDate }: { venueId: string; eventDate: string | null }) {
  const [state, setState] = useState<'loading' | 'open' | 'blocked' | 'held' | 'unset' | 'error'>('loading');
  const [note, setNote] = useState<string | null>(null);

  useEffect(() => {
    if (!eventDate) return;
    let cancelled = false;
    const day = dateKey(eventDate);
    venueAvailability(venueId, day, day)
      .then((r) => {
        if (cancelled) return;
        const entry = r.dates.find((d) => dateKey(d.date) === day);
        if (!entry) { setState('unset'); return; }
        setNote(entry.notes);
        setState(entry.bookedByEvent ? 'held' : entry.available ? 'open' : 'blocked');
      })
      .catch(() => { if (!cancelled) setState('error'); });
    return () => { cancelled = true; };
  }, [venueId, eventDate]);

  if (!eventDate) return null;
  if (state === 'loading') return <span className="org-hint">Checking that date…</span>;
  if (state === 'error') return <span className="org-hint">Calendar unavailable.</span>;

  const copy: Record<string, string> = {
    open: 'Marked open on your event date.',
    blocked: 'Marked unavailable on your event date.',
    held: 'Already held by another confirmed booking on your date.',
    unset: 'The venue has not marked that date either way.',
  };
  const tone = state === 'open' ? 'ven-ok' : state === 'unset' ? '' : 'ven-warn';

  return (
    <span className={`org-hint ${tone}`}>
      {copy[state]}{note ? ` (${note})` : ''}
    </span>
  );
}

export function VenuePicker({
  eventDate = null,
  expectedSize = null,
  sizeLabel = 'expected attendance',
  onSelect,
  selectLabel = 'Request booking',
  busyVenueId = null,
  disabledReason = null,
}: {
  /** When known, the picker checks each venue's calendar for this date. */
  eventDate?: string | null;
  /**
   * Rooms smaller than this are flagged, never hidden. The number means different
   * things depending on where it came from — /events/mine exposes ticket capacity
   * while the manage view exposes expected attendance — so the caller names it.
   */
  expectedSize?: number | null;
  sizeLabel?: string;
  onSelect: (venue: DirectoryVenue) => void;
  selectLabel?: string;
  busyVenueId?: string | null;
  /** Set to explain why selection is unavailable (e.g. a venue is already confirmed). */
  disabledReason?: string | null;
}) {
  const [venues, setVenues] = useState<DirectoryVenue[] | null>(null);
  const [markets, setMarkets] = useState<{ city: string; venues: number }[]>([]);
  const [search, setSearch] = useState('');
  const [city, setCity] = useState('');
  const [minCapacity, setMinCapacity] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setError(null);
    browseVenues({
      search: search || undefined,
      city: city || undefined,
      minCapacity: minCapacity.trim() === '' ? null : Number(minCapacity),
    })
      .then(setVenues)
      .catch((err: unknown) =>
        setError(err instanceof ApiError ? err.message : 'Could not load venues.'));
  }, [search, city, minCapacity]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    venueMarkets().then((m) => setMarkets(m.map((x) => ({ city: x.city, venues: x.venues })))).catch(() => {});
  }, []);

  return (
    <div className="venue-browser">
      <form className="adm-filters" onSubmit={(e) => { e.preventDefault(); load(); }}>
        <input
          className="field__input adm-search" type="search" placeholder="Venue or city…"
          value={search} onChange={(e) => setSearch(e.target.value)}
        />
        <select className="adm-select" value={city} onChange={(e) => setCity(e.target.value)}>
          <option value="">All cities</option>
          {markets.map((m) => (
            <option key={m.city} value={m.city}>{m.city} ({m.venues})</option>
          ))}
        </select>
        <input
          className="field__input adm-search mono" type="number" min="0" step="1"
          placeholder="Min capacity"
          value={minCapacity} onChange={(e) => setMinCapacity(e.target.value)}
        />
        <button type="submit" className="button button--secondary">Search</button>
        {(search || city || minCapacity) && (
          <button type="button" className="button button--ghost"
            onClick={() => { setSearch(''); setCity(''); setMinCapacity(''); }}>
            Clear
          </button>
        )}
      </form>

      {expectedSize != null && expectedSize > 0 && (
        <p className="org-hint">
          Matching against your event&rsquo;s {sizeLabel} of <strong>{expectedSize}</strong>.
          Smaller rooms are marked, not hidden, a tight room is a judgement call, not an
          error.
        </p>
      )}

      {error && <p className="org-error" role="alert">{error}</p>}
      {venues === null && !error && <p className="org-hint">Loading venues…</p>}
      {venues?.length === 0 && (
        <p className="org-hint">
          No venues match. Venues appear here as soon as they create a profile, there is
          no approval step for venues today.
        </p>
      )}

      <ul className="venue-grid">
        {venues?.map((v) => {
          const tooSmall = expectedSize != null && expectedSize > 0 && v.capacity != null
            && v.capacity < expectedSize;
          const open = expanded === v.venueId;
          return (
            <li key={v.venueId} className="venue-card">
              <div className="venue-card__head">
                <span className="venue-card__thumb" aria-hidden="true">
                  {v.images[0]
                    ? <img src={v.images[0]} alt="" />
                    : v.venueName.slice(0, 1)}
                </span>
                <div className="venue-card__title-wrap">
                  <button type="button" className="venue-card__name"
                    onClick={() => setExpanded(open ? null : v.venueId)}>
                    {v.venueName}
                  </button>
                  <span className="org-hint">
                    {[v.city, v.state].filter(Boolean).join(', ') || 'Location not set'}
                    {v.capacity != null ? ` · capacity ${v.capacity}` : ' · capacity not stated'}
                  </span>
                  <span className="venue-card__tags">
                    {tooSmall && (
                      <span className="venue-tag venue-tag--warn">
                        Under your {sizeLabel} of {expectedSize}
                      </span>
                    )}
                    {v.confirmedBookings > 0 && (
                      <span className="venue-tag">
                        {v.confirmedBookings} confirmed booking{v.confirmedBookings === 1 ? '' : 's'}
                      </span>
                    )}
                    {!v.approved && <span className="venue-tag venue-tag--muted">Not reviewed</span>}
                  </span>
                </div>
                <button
                  type="button"
                  className="button button--secondary venue-card__cta"
                  disabled={!!disabledReason || busyVenueId === v.venueId}
                  title={disabledReason ?? undefined}
                  onClick={() => onSelect(v)}
                >
                  {busyVenueId === v.venueId ? 'Sending…' : selectLabel}
                </button>
              </div>

              {open && (
                <div className="venue-card__detail">
                  <AvailabilityNote venueId={v.venueId} eventDate={eventDate} />
                  <dl className="venue-specs">
                    {v.capacity != null && (
                      <div><dt>Capacity</dt><dd className="mono">{v.capacity}</dd></div>
                    )}
                    {v.layoutOptions && <div><dt>Layouts</dt><dd>{v.layoutOptions}</dd></div>}
                    {v.technicalSpecs && <div><dt>Technical</dt><dd>{v.technicalSpecs}</dd></div>}
                    {v.pricingStructure && <div><dt>Pricing</dt><dd>{v.pricingStructure}</dd></div>}
                    {v.requirements && <div><dt>Requirements</dt><dd>{v.requirements}</dd></div>}
                  </dl>
                  {!v.layoutOptions && !v.technicalSpecs && !v.pricingStructure && (
                    <p className="org-hint">
                      This venue has not filled in its specs yet. Worth asking before you
                      commit a date.
                    </p>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
